import { useApp } from "@hooks/useApp";
import { useCore } from "@hooks/useCore";
import { useRoutes } from "@hooks/useRoutes";
import { useWindowSize } from "@hooks/useWindowSize";
import { ComponentProps } from "@ts/components";
import { useFilters, type ResultsProps } from "@usereactify/search";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SearchListEmpty } from "../List/SearchListEmpty";
import { SearchListError } from "../List/SearchListError";
import NumberedPagination from "../Pagination/NumberedPagination";
import Pagination from "../../Pagination/Pagination";
import { SearchResultsCalloutCard } from "./SearchResultsCalloutCard";
import SearchResultsProductCard from "./SearchResultsProductCard";

export type SearchResultsInputProps = ComponentProps & {
  collection?: {
    handle?: string;
    title?: string;
  };
};

export type SearchResultsOutputProps = SearchResultsInputProps & {
  ren1erError: (props: any) => JSX.Element;
  renderResultCard: (props: any) => JSX.Element;
  renderResultCardCallout: (props: any) => JSX.Element;
  renderLoadMoreButton: (props: any) => JSX.Element;
  renderPaginationNumbered: (
    props: React.ComponentProps<
      NonNullable<ResultsProps["renderPaginationNumbered"]>
    >,
  ) => JSX.Element;
  renderNoResults: () => JSX.Element;
  isXL: boolean;
  isMD: boolean;
  isMLG: boolean;
  pageSize: number;
  numColumns: number;
};

export const withSearchResults =
  (Component: FC<SearchResultsOutputProps>) =>
  ({ name = "SearchResults", collection }: SearchResultsInputProps) => {
    const {
      windowSize: { isXL, isMD, isMLG },
    } = useWindowSize();
    const cardRenderCount = useRef(0);
    const {
      config: {
        settings: { keys },
        services: {
          reactify: { pageSize: appConfigPageSize },
        },
      },
    } = useApp();
    const {
      helpers: { storage },
    } = useCore();
    const { filterStack } = useFilters() || {};
    const { getCurrentUrl, getUrlParameter, replaceUrl, setUrlParameter } =
      useRoutes();

    const pageSizeParameter = Number(getUrlParameter("pageSize"));
    const defaultPageSize = filterStack?.pageSize || appConfigPageSize;
    const defaultOrCurrentPageSize =
      isNaN(pageSizeParameter) || pageSizeParameter <= 1
        ? defaultPageSize
        : pageSizeParameter;
    const [pageSize, setPageSize] = useState(defaultOrCurrentPageSize);

    const renderError = (props) => {
      return <SearchListError {...props} />;
    };

    const renderNoResults = () => {
      return collection ? <SearchListEmpty /> : null;
    };

    const renderResultCard = useCallback(
      (props) => {
        cardRenderCount.current += 1;
        return <SearchResultsProductCard {...props} collection={collection} />;
      },
      [cardRenderCount],
    );

    const renderResultCardCallout = useCallback(
      (props) => {
        cardRenderCount.current += 1;
        return <SearchResultsCalloutCard {...props} />;
      },
      [cardRenderCount],
    );

    const renderLoadMoreButton = useCallback((props) => {
      const { handleLoadMore: _, ...rest } = props;

      return (
        <Pagination
          {...rest}
          collection={collection}
          handleLoadMore={handleLoadMore}
        />
      );
    }, []);

    const renderPaginationNumbered: SearchResultsOutputProps["renderPaginationNumbered"] =
      useCallback(
        (props) => {
          return <NumberedPagination {...props} />;
        },
        [cardRenderCount],
      );

    const handleLoadMore = () => {
      const currentPage = defaultOrCurrentPageSize;
      const currentUrl = getCurrentUrl();
      const newUrl = setUrlParameter(
        "pageSize",
        currentPage + defaultPageSize,
        currentUrl || undefined,
      );

      setPageSize((prevState) => prevState + defaultPageSize);
      replaceUrl(newUrl, true);
    };

    const calculateColumns = useCallback(() => {
      if (isXL) {
        return 4;
      }
      if (isMD) {
        return 3;
      }
      return 2;
    }, [isMD, isXL]);

    const numColumns = calculateColumns();

    useEffect(() => {
      storage.set(keys.collection_last, collection);
    }, [collection]);

    Component.displayName = name;

    return useMemo(
      () => (
        <Component
          renderError={renderError}
          renderLoadMoreButton={renderLoadMoreButton}
          renderPaginationNumbered={renderPaginationNumbered}
          renderNoResults={renderNoResults}
          renderResultCard={renderResultCard}
          renderResultCardCallout={renderResultCardCallout}
          isXL={isXL}
          isMD={isMD}
          isMLG={isMLG}
          pageSize={pageSize}
          numColumns={numColumns}
          collection={collection}
        />
      ),
      [isXL, isMD, pageSize],
    );
  };
